/* eslint i18next/no-literal-string: 0 */
import React, { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment-mini'

import { RootState } from '../../../store/rootReducer'
import { Member } from '../../../store/commonTypes'
// @ts-ignore
import { FullPmState } from 'pm-js/types/pm'
import { memberLogout } from '../../../store/member/memberSlice'
import { closeMenu, setPageToReturnFromPayment, switchPageLoading } from '../../../store/common/commonSlice'
import { createLocalizedPath } from '../../../utils/localizationHelpers'
import { removePrefixFromPath } from '../../../i18next'
import { pagesRequiringAuthorization } from '../../../constants/pagesRequiringAuthorization'
import intersection from 'lodash/intersection'
import paths from '../../../paths'
import InformationWindow from '../../informationWindow/informationWindow'
import { deleteAccount } from '../../../api/member'
import { createPortal } from 'react-dom'

import {
    IconBell,
    IconMail,
    IconHeart,
    IconUsers,
    IconBan,
    IconGift,
    IconCoin,
    IconCrown,
    IconLogout,
    IconUser,
    IconUserOff,
    IconChevronRight,
    IconBook
} from '@tabler/icons-react'

interface Props {
    user: Member
}

const MenuNewLeft: React.FC<Props> = ({ user }) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()

    const pm = useSelector<RootState, FullPmState>((state) => state.pm)
    const messagesCounters = pm ? pm.counters.requested_by_recipient + pm.counters.allowed_unread : 0

    const [isAccountDeleting, setIsAccountDeleting] = useState(false)
    const [isInformationWindowOpen, setIsInformationWindowOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')

    const counters = user.counters

    const handleMenuClick = () => {
        dispatch(closeMenu())
        dispatch(switchPageLoading())
    }

    const checkIsRequiringAuthorizationPage = () =>
        intersection(pagesRequiringAuthorization, location.pathname.split('/')).length > 0

    const handleLogout = () => {
        if (checkIsRequiringAuthorizationPage())
            history.replace({
                pathname: createLocalizedPath(paths.stub.path),
                search: `?returnPath=${removePrefixFromPath(location.pathname)}`
            })
        setTimeout(() => dispatch(memberLogout()), 10)
    }

    const handleDeleteAccount = () => {
        setIsAccountDeleting(false)
        deleteAccount({ confirmed: 1 })
            .then((res) => {
                setIsInformationWindowOpen(true)
                setSuccessMessage(res.message)
            })
            .catch((e) => console.log(e))
    }

    useEffect(() => {
        if (location.search.includes('delete-account')) {
            setIsAccountDeleting(true)
        }
    }, [])

    const isPremium = user.member_type !== 'free'
    const hasExpiration = user.expiration_date_ts > 0

    const SEVEN_DAYS = 7 * 24 * 60 * 60
    const isExpiringSoon = hasExpiration && user.expiration_date_ts - moment().unix() < SEVEN_DAYS
    const showPremiumBlock = !isPremium || isExpiringSoon

    const activityTiles = [
        {
            label: t('Notifications'),
            icon: <IconBell size={24} />,
            count: counters.notifications,
            active: counters.notifications > 0,
            path: paths.notificationList.path
        },
        {
            label: t('Messages'),
            icon: <IconMail size={24} />,
            count: messagesCounters,
            active: messagesCounters > 0,
            path: paths.memberMessagePmConversations.path
        },
        {
            label: t('My favorites'),
            icon: <IconHeart size={24} />,
            count: 0,
            active: false,
            path: paths.favoriteContributions.path
        },
        {
            label: t('Following_menu'),
            icon: <IconUsers size={24} />,
            count: counters.followers,
            active: counters.followers > 0,
            path: paths.myFollowing.path
        },
        {
            label: t('My blacklist'),
            icon: <IconBan size={24} />,
            count: 0,
            active: false,
            path: paths.myBlacklist.path
        },
        {
            label: t('My virtual gifts'),
            icon: <IconGift size={24} />,
            count: counters.gifts,
            active: counters.gifts > 0,
            path: paths.myVirtualGifts.path
        }
    ]

    const extraLinks = [
        {
            label: t('My membership Premium'),
            icon: <IconCrown size={18} />,
            count: null,
            path: paths.premiumHistory.path
        },
        {
            label: t('My wallet'),
            icon: <IconCoin size={18} />,
            count: counters.wallet_amount,
            path: paths.myWallet.path
        },
        {
            label: t('My guestbook'),
            icon: <IconBook size={18} />,
            count: counters.guestbook,
            path: paths.myGuestbook.path
        },
        {
            label: t('My profile'),
            icon: <IconUser size={18} />,
            count: null,
            path: paths.myProfile.path
        }
    ]

    return (
        <div className="menuNewLeft">
            <div className="menuNewLeft__profile">
                <Link
                    to={createLocalizedPath(paths.myProfile.path)}
                    onClick={handleMenuClick}
                    className="menuNewLeft__avatar"
                >
                    <img src={user.avatar_sm_url || user.avatar_url} alt="" />
                    {isPremium && <span className="menuNewLeft__premiumBadge">{t('PREMIUM')}</span>}
                </Link>
                <div className="menuNewLeft__profileInfo">
                    <Link
                        to={createLocalizedPath(paths.myProfile.path)}
                        onClick={handleMenuClick}
                        className="menuNewLeft__nick"
                    >
                        {user.nick}
                    </Link>
                </div>
            </div>

            <div className="menuNewLeft__tiles">
                {activityTiles.map((tile) => (
                    <Link
                        key={tile.label}
                        to={createLocalizedPath(tile.path)}
                        onClick={handleMenuClick}
                        className="menuNewLeft__tile"
                    >
                        <div className="menuNewLeft__tileIcon">
                            {tile.icon}
                            {tile.count > 0 && (
                                <span
                                    className={`menuNewLeft__tileBadge${
                                        tile.active ? ' menuNewLeft__tileBadge--active' : ''
                                    }`}
                                >
                                    {tile.count > 99 ? '99+' : tile.count}
                                </span>
                            )}
                        </div>
                        <span className="menuNewLeft__tileLabel">{tile.label}</span>
                    </Link>
                ))}
            </div>

            {showPremiumBlock && (
                <div className="menuNewLeft__premium">
                    <div className="menuNewLeft__premiumIcon">
                        <IconCrown size={20} />
                    </div>
                    <div className="menuNewLeft__premiumText">
                        <span className="menuNewLeft__premiumTitle">{t('Premium membership')}</span>
                        {isPremium && hasExpiration ? (
                            <span className="menuNewLeft__premiumSub">
                                {t('Expiration') + ' ' + moment.unix(user.expiration_date_ts).format(t('time format'))}
                            </span>
                        ) : (
                            <></>
                        )}
                    </div>
                    <Link
                        to={createLocalizedPath(
                            paths.order.path + '?utm_source=bobvoyeur&utm_medium=web&utm_campaign=userBlock_menu'
                        )}
                        onClick={() => {
                            dispatch(closeMenu())
                            dispatch(switchPageLoading())
                            dispatch(setPageToReturnFromPayment(location.pathname))
                        }}
                        className="menuNewLeft__premiumLink"
                    >
                        {isPremium ? t('Buy more Premium') : t('Access Premium features')}
                        <IconChevronRight size={14} />
                    </Link>
                </div>
            )}

            <div className="menuNewLeft__extraLinks">
                {extraLinks.map((link) => (
                    <Link
                        key={link.label}
                        to={createLocalizedPath(link.path)}
                        onClick={handleMenuClick}
                        className="menuNewLeft__extraLink"
                    >
                        <span className="menuNewLeft__extraIcon">{link.icon}</span>
                        <span className="menuNewLeft__extraLabel">{link.label}</span>
                        {link.count != null && link.count > 0 && (
                            <span className="menuNewLeft__extraCount">{link.count}</span>
                        )}
                        <IconChevronRight size={14} className="menuNewLeft__extraArrow" />
                    </Link>
                ))}
                <button
                    className="menuNewLeft__extraLink menuNewLeft__extraLink"
                    onClick={() => setIsAccountDeleting(true)}
                >
                    <span className="menuNewLeft__extraIcon">
                        <IconUserOff size={18} />
                    </span>
                    <span className="menuNewLeft__extraLabel">{t('Delete my account')}</span>
                </button>
                <button className="menuNewLeft__extraLink menuNewLeft__extraLink--danger" onClick={handleLogout}>
                    <span className="menuNewLeft__extraIcon">
                        <IconLogout size={18} />
                    </span>
                    <span className="menuNewLeft__extraLabel">{t('Log out')}</span>
                </button>
            </div>

            {typeof window !== 'undefined' &&
                createPortal(
                    <>
                        <InformationWindow
                            title={t('Delete my account')}
                            informationText={t('You can request deletion', {
                                postProcess: 'interval',
                                nick: user.nick
                            })}
                            secondParagraph={t('All data in this account will be deleted')}
                            thirdParagraph={t('Your request will not be cancelable')}
                            isJustify
                            externalState={isAccountDeleting}
                            setExternalState={setIsAccountDeleting}
                            labelFunctionalButton={t('Delete my account')}
                            closeButton
                            functionalButtonCallback={handleDeleteAccount}
                            showCloseIcon={true}
                        />
                        <InformationWindow
                            externalState={isInformationWindowOpen}
                            informationText={successMessage}
                            setExternalState={setIsInformationWindowOpen}
                            title={' '}
                            labelFunctionalButton={t('Ok')}
                            functionalButtonCallback={() => {
                                setIsInformationWindowOpen(false)
                                dispatch(memberLogout())
                            }}
                        />
                    </>,
                    document.body
                )}
        </div>
    )
}

export default MenuNewLeft
